import { z } from "zod";
import { QUESTION_TYPES, VALUE_TYPES } from "@/isomorphic/constants/call-data-consts";

/**
 * Collected Answer Schema (Isomorphic - camelCase)
 * Represents structured and validated answers from patients
 */
export const CollectedAnswerSchema = z.object({
  id: z.string().uuid(),
  callAttemptId: z.string().uuid(),

  // Lien avec le transcript
  transcriptSegmentId: z.string().uuid().nullable(),

  // Question liée (nullable pour info spontanée)
  questionId: z.string().nullable(),
  questionType: z.enum(QUESTION_TYPES),

  // Valeur collectée
  value: z.unknown(), // JSONB - peut être string, number, boolean, object, array
  valueType: z.enum(VALUE_TYPES),

  // Statut
  isFinal: z.boolean().default(false),

  // Participant qui a répondu
  respondentParticipantId: z.string().uuid().nullable(),

  // Fusion de réponses (si plusieurs réponses à la même question)
  mergedIntoAnswerId: z.string().uuid().nullable(),

  // Validation
  confidenceScore: z.number().min(0).max(1).nullable(),
  validatedBy: z.string().uuid().nullable(),
  validatedAt: z.date().nullable(),

  // Métadonnées
  notes: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).default({}),

  createdAt: z.date(),
  updatedAt: z.date(),
});

export const UpdateCollectedAnswerBodySchema = z.object({
  value: z.unknown().optional(),
  isFinal: z.boolean().optional(),
  confidenceScore: z.number().min(0).max(1).optional(),
  notes: z.string().optional(),
});

/**
 * Validate collected answer body schema
 */
export const ValidateCollectedAnswerBodySchema = z.object({
  notes: z.string().optional(),
});

/**
 * Final only filter (for collected answers)
 */
export const FinalOnlyQuerySchema = z.object({
  finalOnly: z.coerce.boolean().default(false),
});

/**
 * Query params for filtering collected answers
 */
export const CollectedAnswersQuerySchema = z.object({
  finalOnly: z.coerce.boolean().default(false),
  questionId: z.string().optional(),
  followUpId: z.string().uuid().optional(),
});

export type CollectedAnswer = z.infer<typeof CollectedAnswerSchema>;
export type UpdateCollectedAnswerBody = z.infer<typeof UpdateCollectedAnswerBodySchema>;
export type ValidateCollectedAnswerBody = z.infer<typeof ValidateCollectedAnswerBodySchema>;
export type FinalOnlyQuery = z.infer<typeof FinalOnlyQuerySchema>;
export type CollectedAnswersQuery = z.infer<typeof CollectedAnswersQuerySchema>;
